import type { ReactNode } from "react";
import { format } from "date-fns";
import { FileImage, FileText, ShieldAlert, ShieldCheck } from "lucide-react";
import { DataCard } from "@/components/ui/data-card";

interface DocumentCardProps {
  fileName: string;
  documentType: string | null;
  createdAt: string;
  uploadedByEmail: string | null;
  primaryId: string | null;
  carrier: string | null;
  isVerified: boolean;
  status: string | null;
  /** Rendered in the header (status badge). */
  badge?: ReactNode;
  onClick?: () => void;
}

/** Mobile/tablet representation of a document history row. */
export function DocumentCard({
  fileName,
  documentType,
  createdAt,
  uploadedByEmail,
  primaryId,
  carrier,
  isVerified,
  status,
  badge,
  onClick,
}: DocumentCardProps) {
  return (
    <DataCard onClick={onClick}>
      <DataCard.Header>
        {documentType === "tender" ? (
          <FileText className="h-4 w-4 text-muted-foreground shrink-0" />
        ) : (
          <FileImage className="h-4 w-4 text-muted-foreground shrink-0" />
        )}
        <span className="font-medium text-sm truncate">{fileName}</span>
        {badge ? <span className="ml-auto">{badge}</span> : null}
      </DataCard.Header>

      <DataCard.Chips>
        <span className="text-xs text-muted-foreground">
          {documentType === "tender" ? "Load Tender" : "Bill of Lading"}
        </span>
        {isVerified ? (
          <span className="inline-flex items-center gap-1 text-xs text-primary">
            <ShieldCheck className="h-3 w-3" /> Verified
          </span>
        ) : status === "completed" ? (
          <span className="inline-flex items-center gap-1 text-xs text-amber-600 dark:text-amber-400">
            <ShieldAlert className="h-3 w-3" /> Needs verification
          </span>
        ) : null}
      </DataCard.Chips>

      <DataCard.Body>
        <DataCard.Field label="ID / Number">{primaryId ?? "—"}</DataCard.Field>
        <DataCard.Field label="Carrier">{carrier ?? "—"}</DataCard.Field>
        <DataCard.Field label="Uploaded by">{uploadedByEmail ?? "—"}</DataCard.Field>
        <DataCard.Field label="Uploaded">{format(new Date(createdAt), "MMM d, yyyy h:mm a")}</DataCard.Field>
      </DataCard.Body>
    </DataCard>
  );
}